<script>
  // Streifen rechts im Framework Assistant: das Framework der gewaehlten Abteilung waechst sichtbar mit.
  // Vorhanden (gespeichert) plus der aktuelle Vorschlag des Assistenten; was der Vorschlag neu bringt, ist markiert.
  // Ansicht "Overview" mit L1-L4 als Kennzahlen, "List" als Baum. Darunter immer ein Vorschlag fuer den naechsten
  // Schritt. Aus einem Projekt geoeffnet zusaetzlich die Team-Umfragen des Projekts mit ihrem Stand - sie sind fuer die
  // meisten der zentrale Weg zu den Aufgaben (Umfrage 1) und zur Auswahl fuer Task Mining (Umfrage 2).
  import { createEventDispatcher, getContext } from "svelte";
  import Button from "lib/components/Button.svelte";
  import MultiSelect from "lib/components/MultiSelect.svelte";
  import Modal from "lib/components/Modal.svelte";
  import { TextInput } from "paxray-components-svelte";
  import { Dropdown } from "carbon-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  export let areas = [];
  export let draft = [];
  export let savedVersion = 0; // aendert sich nach dem Speichern - dann das vorhandene Framework neu laden
  export let inProject = false;
  export let projectId = "";
  export let surveys = [];
  // Kopf: Abteilung als Auswahl (ein Projekt kann mehrere haben), daneben die Teams - waehlen und hinzufuegen
  // export let departmentOptions = [];
  export let teams = [];
  export let selectedTeams = [];
  let newTeamName = "";
  let addingTeam = false;
  let savingTeam = false;
  let teamSaveError = "";

  const teamKey = (team) =>
    team.id || `new:${team.name.trim().toLocaleLowerCase()}`;
  const isSelected = (team) =>
    selectedTeams.some((item) => teamKey(item) === teamKey(team));
  $: availableTeams = [...teams, ...selectedTeams.filter((team) => !team.id)];
  $: teamOptions = availableTeams.map((team) => ({
    id: teamKey(team),
    text: team.name,
  }));
  $: selectedTeamIds = selectedTeams.map(teamKey);

  function selectTeams(event) {
    const selectedIds = event.detail.selectedIds;
    dispatch(
      "teams",
      availableTeams
        .filter((team) => selectedIds.includes(teamKey(team)))
        .map((team) =>
          team.id ? { id: team.id, name: team.name } : { name: team.name },
        ),
    );
  }

  async function addTeam() {
    const name = newTeamName.trim();
    if (!name || savingTeam || !projectId) return;
    savingTeam = true;
    teamSaveError = "";
    try {
      const response = await fetchWithSession(
        `/api/damprojects/dam-teams?idproject=${encodeURIComponent(projectId)}`,
      );
      if (!response.ok)
        throw new Error("Could not load project teams. Please try again.");
      const existingTeams = await response.json();
      let team = existingTeams.find(
        (item) =>
          item.name.trim().toLocaleLowerCase() === name.toLocaleLowerCase(),
      );
      if (!team) {
        const createResponse = await fetchWithSession(
          "/api/damprojects/dam-teams",
          {
            method: "POST",
            body: JSON.stringify({
              name,
              idproject: projectId,
              damUsers: [],
              subprocesses: [],
            }),
          },
        );
        const result = await createResponse.json();
        if (!createResponse.ok || !result.ok || !result.data?.id) {
          throw new Error(
            result.message ||
              result.error ||
              "Could not save the team. Please try again.",
          );
        }
        team = result.data;
      }
      if (!teams.some((item) => item.id === team.id)) teams = [...teams, team];
      const selection = selectedTeams.filter(
        (item) =>
          item.id ||
          item.name.trim().toLocaleLowerCase() !== name.toLocaleLowerCase(),
      );
      dispatch(
        "teams",
        isSelected(team)
          ? selection
          : [...selection, { id: team.id, name: team.name }],
      );
      dispatch("teamsCreated");
      newTeamName = "";
      addingTeam = false;
    } catch (error) {
      teamSaveError =
        error.message || "Could not save the team. Please try again.";
    } finally {
      savingTeam = false;
    }
  }

  const dispatch = createEventDispatcher();

  const LEVELS = [
    { key: "l1", label: "Process", plural: "Processes", short: "L1" },
    { key: "l2", label: "Subprocess", plural: "Subprocesses", short: "L2" },
    { key: "l3", label: "Task", plural: "Tasks", short: "L3" },
    { key: "l4", label: "Subtask", plural: "Subtasks", short: "L4" },
  ];
  let view = "overview";
  let existing = [];
  let loadedFor = "";
  let error = "";

  $: department = areas[0] || "";
  $: if (department && `${department}|${savedVersion}` !== loadedFor)
    load(department, savedVersion);
  $: tree = merge(existing, draft);
  $: counts = countLevels(tree);
  $: pending = LEVELS.some((level) => counts[level.key].added);
  $: tasksWithoutSubtasks = tree
    .flatMap((l1) => l1.children)
    .flatMap((l2) => l2.children)
    .filter((l3) => !l3.children.length).length;
  $: collecting = surveys.find((survey) => survey.stage === "collect");
  $: choosing = surveys.find((survey) => survey.stage === "select");
  // Uebernehmen (Transfer) vorerst nur fuer Admins - sonst kein Vorschlag dazu
  const role = getContext("role");
  $: collected =
    $role?.role === "admin" &&
    surveys.find(
      (survey) => survey.stage === "done" && survey.mode !== "select",
    );
  // Ein Vorschlag mit hoechstens einer Aktion: survey = neue Umfrage (Modus), manage = Umfragen oeffnen
  $: next = !department
    ? { text: "Choose the department you want to set up." }
    : pending
      ? { text: "Check the proposal on the left and confirm it to save it." }
      : collecting
        ? {
            text: `${surveys.filter((survey) => survey.stage === "collect").length > 1 ? "Your teams are" : "Your team is"} listing their tasks (${surveys.filter((survey) => survey.stage === "collect").reduce((sum, survey) => sum + survey.answers.collect, 0)} answered). Wait for them or send a reminder.`,
            manage: "Open the team survey",
          }
        : choosing
          ? {
              text: "Your team is choosing what Task Mining records.",
              manage: "Open the team survey",
            }
          : collected && (!counts.l1.total || tasksWithoutSubtasks)
            ? {
                text: `The team survey of ${collected.team} is finished. Take over its tasks. Pax AI suggests and you confirm.`,
                manage: "Take over the tasks",
                transfer: collected.id,
              }
            : !counts.l1.total
              ? {
                  text: "Describe the work or upload documents, or ask your team for their tasks first.",
                  survey: "collect",
                  label: "Ask my team for their tasks",
                }
              : tasksWithoutSubtasks
                ? {
                    text: `${tasksWithoutSubtasks} of ${counts.l3.total} tasks have no subtasks yet. Let your team add them or describe more on the left.`,
                    survey: "collect",
                    label: "Let my team add the subtasks",
                  }
                : {
                    text: "Every task has subtasks. Check the framework and the scope in Configure Framework.",
                    configure: true,
                  };
  // Die Team-Umfrage als Kachel neben den Kennzahlen; ein Klick oeffnet die Verwaltung als Fenster.
  $: surveyTiles = [
    tile(
      "Survey · Collect tasks",
      surveys.filter((survey) => survey.mode !== "select"),
      "collect",
      (survey) =>
        `${survey.answers.collect}${survey.invites.total ? ` of ${survey.invites.total}` : ""} answered`,
      (done) =>
        `${done.reduce((sum, survey) => sum + (survey.tasks || 0), 0)} tasks collected`,
      "collect",
    ),
  ];

  function tile(title, list, activeStage, activeText, doneText, newMode) {
    const actives = list.filter((survey) => survey.stage === activeStage);
    const active = actives[0];
    const done = list.filter(
      (survey) =>
        survey.stage === "done" ||
        (activeStage === "collect" && survey.stage === "select"),
    );
    const until = (survey) =>
      activeStage === "collect" ? survey.collect_until : survey.select_until;
    // mehrere Teams gleichzeitig: zusammengezaehlt, mit der fruehesten Frist
    if (actives.length > 1) {
      const answered = actives.reduce(
        (sum, survey) => sum + survey.answers[activeStage],
        0,
      );
      const invited = actives.reduce(
        (sum, survey) => sum + survey.invites.total,
        0,
      );
      const first = actives.map(until).sort()[0];
      return {
        title,
        state: "active",
        text: `${actives.length} teams · ${answered}${invited ? ` of ${invited}` : ""} ${activeStage === "collect" ? "answered" : "voted"} · until ${formatDate(first)}`,
      };
    }
    if (active)
      return {
        title,
        state: "active",
        text: `${activeText(active)} · until ${formatDate(until(active))}`,
      };
    if (done.length) return { title, state: "done", text: doneText(done) };
    return {
      title,
      state: "new",
      text: "Not started. Click to set up",
      mode: newMode,
    };
  }

  function formatDate(value) {
    return new Date(value).toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  }

  async function load(name, version) {
    loadedFor = `${name}|${version}`;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/paxbot/framework-assistant/department-framework?area=${encodeURIComponent(name)}`,
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.message);
      existing = (result.framework || []).map((process) => ({
        name: process.l1,
        l2: process.l2,
      }));
      error = "";
    } catch {
      error = "The framework could not be loaded.";
    }
  }

  const key = (name) =>
    String(name || "")
      .trim()
      .toLocaleLowerCase();

  // Ein Baum aus Vorhandenem und Vorschlag; added = nur im Vorschlag
  function merge(saved, proposal) {
    const result = [];
    const add = (list, name, added) => {
      let node = list.find((item) => key(item.name) === key(name));
      if (!node) {
        node = { name, added, children: [] };
        list.push(node);
      }
      return node;
    };
    [
      [saved, false],
      [proposal, true],
    ].forEach(([framework, added]) => {
      (framework || []).forEach((l1) => {
        const n1 = add(result, l1.name, added);
        (l1.l2 || []).forEach((l2) => {
          const n2 = add(n1.children, l2.name, added);
          (l2.l3 || []).forEach((l3) => {
            const n3 = add(n2.children, l3.name, added);
            (l3.l4 || [])
              .filter(Boolean)
              .forEach((l4) => add(n3.children, l4, added));
          });
        });
      });
    });
    return result;
  }

  function countLevels(nodes) {
    const result = Object.fromEntries(
      LEVELS.map((level) => [level.key, { total: 0, added: 0 }]),
    );
    const walk = (list, depth) =>
      list.forEach((node) => {
        const level = result[LEVELS[depth].key];
        level.total += 1;
        level.added += node.added ? 1 : 0;
        if (depth < 3) walk(node.children, depth + 1);
      });
    walk(nodes, 0);
    return result;
  }
</script>

<aside class="growth-panel" aria-label="Framework overview">
  <header>
    <div>
      <!-- Vorerst kein Department-Wechsel im Streifen: Department ist Single Select und steht nach der Auswahl fest -->
      <!-- {#if inProject && departmentOptions.length}
        <div class="teams-editor" title={pending ? "Confirm or discard the proposal first" : "Department"}>
          <Dropdown
            titleText="Department"
            label="Choose a department"
            items={departmentOptions.map((name) => ({ id: name, text: name }))}
            selectedId={department}
            disabled={pending}
            size="sm"
            on:select={(event) => dispatch("department", event.detail.selectedId)}
          />
        </div>
      {:else} -->
      <span class="eyebrow">Department</span>
      <strong>{department || "No department yet"}</strong>
      <!-- {/if} -->
      <!-- Vorerst keine Teamauswahl im Streifen (Team-Schritt im Assistant ist ebenfalls deaktiviert) -->
      <!-- {#if inProject && department}
          <div class="teams-editor">
            <MultiSelect
              labelText="Teams"
              label="Select teams"
              selectionLabel={selectedTeams.length > 1 ? `${selectedTeams.length} teams` : selectedTeams[0]?.name || ""}
              items={teamOptions}
              selectedIds={selectedTeamIds}
              size="sm"
              onchange={selectTeams}
            />
            <Button variant="paxAI" size="sm" width="100%" text="Add a team" disabled={savingTeam} on:click={() => { newTeamName = ""; teamSaveError = ""; addingTeam = true; }} />
          </div>
      {/if} -->
    </div>
    {#if counts.l1.total}
      <div class="switch" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === "overview"}
          class:active={view === "overview"}
          on:click={() => (view = "overview")}>Overview</button
        >
        <button
          type="button"
          role="tab"
          aria-selected={view === "list"}
          class:active={view === "list"}
          on:click={() => (view = "list")}>List</button
        >
      </div>
    {/if}
  </header>

  {#if !department}
    <p class="hint">
      Choose a department to see its framework grow here while you work.
    </p>
  {:else if error}
    <p class="hint">{error}</p>
  {:else if !counts.l1.total}
    <p class="hint">
      Your framework will take shape here as Pax AI turns your descriptions or
      uploaded documents into processes, tasks and subtasks. The first entries
      will appear after your input is analyzed.
    </p>
  {:else if view === "overview"}
    <div class="kpis">
      {#each LEVELS as level}
        <div class="kpi">
          <span class="kpi-label"
            >{counts[level.key].total === 1 ? level.label : level.plural} · {level.short}</span
          >
          <span class="kpi-value">{counts[level.key].total}</span>
          {#if counts[level.key].added}<span class="kpi-added"
              >+{counts[level.key].added} in the proposal</span
            >{/if}
        </div>
      {/each}
    </div>
    {#if pending}
      <p class="hint">
        Marked additions are a proposal. They are saved when you confirm it.
      </p>
    {/if}
  {:else}
    <ul class="tree">
      {#each tree as l1}
        <li class:added={l1.added}>
          <span class="node l1">{l1.name}</span>
          <ul>
            {#each l1.children as l2}
              <li class:added={l2.added}>
                <span class="node">{l2.name}</span>
                <ul>
                  {#each l2.children as l3}
                    <li class:added={l3.added}>
                      <span class="node">{l3.name}</span>
                      <ul>
                        {#each l3.children as l4}<li class:added={l4.added}>
                            <span class="node l4">{l4.name}</span>
                          </li>{/each}
                      </ul>
                    </li>
                  {/each}
                </ul>
              </li>
            {/each}
          </ul>
        </li>
      {:else}
        <li class="hint">Nothing here yet.</li>
      {/each}
    </ul>
  {/if}

  {#if inProject && department && !error && counts.l1.total}
    <div class="framework-table-action">
      <Button
        variant="paxAI"
        size="sm"
        width="100%"
        text="Open the framework table"
        on:click={() => dispatch("configure")}
      />
    </div>
  {/if}

  <!-- Bereich nur, wenn es schon eine Team-Umfrage gibt -->
  {#if inProject && department && surveys.length}
    <div class="surveys">
      <span class="eyebrow">Team surveys</span>
      {#each surveyTiles as item}
        <button
          type="button"
          class="survey-tile {item.state}"
          on:click={() =>
            item.mode
              ? dispatch("newSurvey", item.mode)
              : dispatch("manageSurveys")}
        >
          <span class="tile-head">
            <span>{item.title}</span>
            <span class="tile-state"
              >{#if item.state === "active"}<span class="dot" aria-hidden="true"
                ></span>active{:else if item.state === "done"}✓ finished{:else}+
                start{/if}</span
            >
          </span>
          <span class="tile-text">{item.text}</span>
        </button>
      {/each}
    </div>
  {/if}

  {#if department && !error}
    <div class="next">
      <span class="eyebrow">Suggested next step</span>
      <p>{next.text}</p>
      {#if inProject && next.manage}<Button
          variant="paxAI"
          size="sm"
          width="100%"
          text={next.manage}
          on:click={() => dispatch("manageSurveys", next.transfer)}
        />{/if}
      {#if inProject && next.survey}<Button
          variant="paxAI"
          size="sm"
          width="100%"
          text={next.label}
          on:click={() => dispatch("newSurvey", next.survey)}
        />{/if}
    </div>
  {/if}
</aside>

<Modal
  bind:open={addingTeam}
  modalHeading="Add a team"
  modalClass="framework-team-modal"
  autowidth
  buttonSize="sm"
  primaryButtonText={savingTeam ? "Adding…" : "Add team"}
  primaryButtonVariant="secondary"
  primaryButtonValidations={savingTeam}
  primaryButtonNotAllow={!newTeamName.trim() || !projectId}
  primaryButtonAction={addTeam}
  actions
>
  <TextInput
    id="framework-team-name"
    labelText="Team name"
    placeholder="e.g. Accounts Payable"
    bind:value={newTeamName}
    disabled={savingTeam}
  />
  {#if teamSaveError}<p class="team-save-error" role="alert">
      {teamSaveError}
    </p>{/if}
</Modal>

<style>
  .team-save-error {
    margin-top: var(--control-gap);
    color: var(--pax-danger);
    font-size: 0.85rem;
  }
  :global(.custom-modal.framework-team-modal .bx--modal-container) {
    width: min(26rem, calc(100vw - 2rem)) !important;
  }
  :global(.custom-modal.framework-team-modal .bx--modal-content) {
    padding-bottom: var(--control-gap) !important;
  }
  :global(
      .custom-modal.framework-team-modal
        .bx--modal-scroll-content
        > .modal_wrapper
    ) {
    padding-bottom: 0 !important;
  }
  :global(.custom-modal.framework-team-modal .divider) {
    margin-bottom: var(--control-gap);
  }
  :global(.custom-modal.framework-team-modal .divider-margin) {
    margin-top: var(--control-gap);
  }
  :global(.custom-modal.framework-team-modal .buttons-container) {
    margin-top: 0;
  }
  :global(.custom-modal.framework-team-modal .actions-buttons-container) {
    width: 100%;
    margin-left: 0;
    justify-content: space-between;
  }

  .growth-panel {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    box-sizing: border-box;
    width: 20rem;
    height: 100%;
    padding: var(--control-gap) 1rem;
    overflow-y: auto;
    border-left: 1px solid var(--pax-border-new);
    background: var(--pax-bg-canvas-new);
    color: var(--pax-text-primary-new);
  }
  header {
    display: grid;
    gap: var(--control-gap);
  }
  /* minmax(0, 1fr): die Kopfzeile darf schmaler werden als ein langer Abteilungsname - sonst lief der Streifen seitlich ueber */
  header > div:first-child {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--control-gap);
  }
  .eyebrow {
    color: var(--pax-text-secondary-new);
    font-size: 0.72rem;
    letter-spacing: 0.04em;
  }
  header strong {
    font-size: 0.95rem;
  }
  .teams-editor {
    display: grid;
    gap: var(--control-gap);
    min-width: 0;
  }
  .teams-editor :global(.bx--label) {
    color: var(--pax-text-secondary-new);
  }
  .teams-editor :global(.bx--list-box) {
    border-color: var(--pax-border-strong-new) !important;
    background: var(--pax-surface-control-new);
  }
  .teams-editor :global(.bx--list-box__label) {
    color: var(--pax-text-primary-new);
    max-width: calc(100% - 1.5rem);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .teams-editor :global(.bx--list-box__menu-icon svg) {
    fill: var(--pax-text-primary-new);
  }
  .switch {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 0.2rem;
    border: 1px solid var(--pax-border-strong-new);
    border-radius: 0.5rem;
    background: var(--pax-surface-control-new);
  }
  .switch button {
    padding: 0.35rem;
    border: 0;
    border-radius: 0.35rem;
    background: transparent;
    color: var(--pax-text-secondary-new);
    font: inherit;
    font-size: 0.8rem;
    cursor: pointer;
  }
  .switch button.active {
    background: var(--pax-white-new);
    color: var(--pax-text-inverse-new);
    font-weight: 600;
  }
  .kpis {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--control-gap);
  }
  .kpi {
    display: grid;
    gap: var(--control-gap);
    padding: 0.75rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 0.6rem;
    background: var(--pax-surface-control-new);
  }
  .kpi-label {
    color: var(--pax-text-secondary-new);
    font-size: 0.72rem;
  }
  .kpi-value {
    font-size: 1.6rem;
    font-weight: 600;
    line-height: 1.1;
  }
  .kpi-added {
    color: #7ee2a8;
    font-size: 0.72rem;
    font-weight: 600;
  }
  .hint {
    margin: 0;
    color: var(--pax-text-secondary-new);
    font-size: 0.8rem;
    line-height: 1.45;
  }
  .tree,
  .tree ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .tree ul {
    margin-left: 0.4rem;
    padding-left: 0.7rem;
    border-left: 1px solid var(--pax-border-new);
  }
  .tree li {
    display: block;
    margin: 0.2rem 0;
    font-size: 0.8rem;
  } /* global ist li inline-block */
  .node {
    display: inline-block;
    line-height: 1.35;
  }
  .node.l1 {
    font-weight: 600;
  }
  .node.l4 {
    color: var(--pax-text-secondary-new);
  }
  li.added > .node {
    color: #7ee2a8;
  }
  li.added > .node::before {
    content: "+ ";
    font-weight: 700;
  }
  .framework-table-action {
    padding-bottom: var(--control-gap);
  }
  .surveys {
    display: grid;
    gap: var(--control-gap);
    margin-inline: -1rem;
    padding: var(--control-gap) 1rem 0;
    border-top: 1px solid var(--pax-border-new);
  }
  .survey-tile {
    display: grid;
    gap: var(--control-gap);
    padding: 0.75rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 0.6rem;
    background: var(--pax-surface-control-new);
    color: var(--pax-text-primary-new);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .survey-tile:hover {
    border-color: var(--pax-border-strong-new);
  }
  .survey-tile.new {
    border-style: dashed;
    background: transparent;
  }
  .survey-tile.active {
    border-color: #7ee2a8;
  }
  .tile-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--control-gap);
    color: var(--pax-text-secondary-new);
    font-size: 0.72rem;
  }
  .tile-state {
    display: inline-flex;
    align-items: center;
    gap: var(--control-gap);
    font-weight: 600;
  }
  .survey-tile.active .tile-state {
    color: #7ee2a8;
  }
  .survey-tile.done .tile-state {
    color: var(--pax-text-primary-new);
  }
  .dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #7ee2a8;
    animation: pulse 1.6s ease-in-out infinite;
  }
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }
  .tile-text {
    font-size: 0.8rem;
  }
  .next {
    display: grid;
    gap: var(--control-gap);
    margin-top: auto;
    margin-inline: -1rem;
    padding: var(--control-gap) 1rem 0;
    border-top: 1px solid var(--pax-border-new);
  }
  .next p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.45;
  }
</style>
